import { useCallback, useEffect, useRef, useState } from "react";
import { showToast } from "services/utils/Status";
import { loadReadyForPayrollCount } from "./shiftEarningsApi";

const POLL_MS = 5 * 60 * 1000;

// Same behaviour as main's Shift Earnings screen: when the period has
// attendance records still on their way to payroll, `waiting` is true (the
// screen shows a "may be incomplete" banner with Refresh), an info toast
// says so, and the count is checked again every 5 minutes until it reaches 0.
// check() re-checks now (the banner's Refresh). A failed check is ignored.
export const useReadyForPayroll = (domain, period, enabled = true) => {
  const [waiting, setWaiting] = useState(false);
  const timerRef = useRef(null);
  const controllerRef = useRef(null);

  const stop = () => {
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
    controllerRef.current?.abort();
  };

  const run = useCallback(async (announce) => {
    stop();
    if (!enabled || !domain || !period?.start || !period?.end) {
      setWaiting(false);
      return;
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      const count = await loadReadyForPayrollCount(domain, period, controller.signal);
      if (controller.signal.aborted) return;
      setWaiting(count > 0);
      if (count > 0) {
        if (announce) showToast("info", "Your payroll data is currently being processed. Please wait.");
        timerRef.current = window.setTimeout(() => run(false), POLL_MS);
      }
    } catch (error) {
      // Leave the screen as it is; the next check (or Refresh) tries again.
    }
  }, [domain, period, enabled]);

  useEffect(() => {
    run(true);
    return stop;
  }, [run]);

  // Refresh re-checks like main: the toast shows again while still waiting.
  const check = useCallback(() => run(true), [run]);
  return { waiting, check };
};
